<template>
  <div class="ins-segbar">
    <!-- строка 0: созданные столбцы. Без неё переключаться между ними можно было бы
         только кликом по шапке, а понять, сколько их всего, — никак. -->
    <div v-if="segments.length" class="ins-segrow">
      <span class="tlabel ins-seg-lab">столбцы</span>
      <button v-for="sg in segments" :key="sg.id" type="button" class="tgl ins-seg-tab"
              :aria-pressed="String(editId === sg.id)" data-help="сегмент/вкладка"
              :title="autoName(sg.parts)"
              @click="$emit('tab', sg.id)">{{ sg.name }}</button>
      <button type="button" class="tgl ins-seg-tab ins-seg-new" data-help="сегмент/новый"
              @click="onNew">+ новый</button>
    </div>

    <!-- строка 1: название столбца -->
    <div class="ins-segrow">
      <span class="tlabel ins-seg-lab">название столбца</span>
      <input class="btn ins-seg-in" data-help="сегмент/имя" :maxlength="nameMax"
             :value="nameInput" :placeholder="placeholder" :title="fullName ? 'полное имя состава: ' + fullName : ''"
             @input="onName" @blur="onNameBlur" @keydown.enter="onNameEnter">
      <span class="ins-seg-len num" :class="{ 'is-warn': lenUsed > nameMax }" :title="lenTitle">{{ lenText }}</span>
      <button v-if="editId === null" type="button" class="btn ins-seg-act" data-help="сегмент/добавить"
              :disabled="!parts.length" @click="commit">добавить столбец</button>
      <!-- выходим из правки, но режим набора не гасим: следующий столбец собирается сразу -->
      <button v-else type="button" class="btn ins-seg-act" data-help="сегмент/готово"
              @click="$emit('done')">готово</button>
    </div>

    <!-- строка 2: поиск и «вся таблица» -->
    <div class="ins-segrow">
      <span class="tlabel ins-seg-lab">{{ editId !== null ? 'правка состава' : 'состав' }}</span>
      <input class="btn ins-seg-in ins-seg-find" data-help="сегмент/поиск" v-model="find"
             placeholder="+ фраза: ищи по тем, которых тут ещё нет" @keydown.enter="onFindEnter">
      <!-- Один столбец по всей таблице — частый случай, ради которого иначе надо прокликать
           все слова. Повторное нажатие снимает: кнопка работает как «всё / ничего». -->
      <button type="button" class="btn" data-help="сегмент/вся таблица"
              :title="full ? 'сейчас нажатие очистит состав' : null"
              @click="$emit('parts', full ? [] : everyPart)">{{ full ? 'снять всё' : 'вся таблица' }}</button>
      <span class="ins-seg-hit">
        {{ editId !== null ? 'правишь имя и состав столбца' : 'кликай по словам в шапке — слово войдёт целиком' }}
      </span>
    </div>

    <!-- строка 3: что набрано. Каркас выше не пересоздаётся при правке состава —
         иначе поле ввода теряло бы фокус после каждой добавленной фразы. -->
    <div class="ins-segrow">
      <template v-if="query">
        <span class="ins-seg-sug">
          <span class="tlabel">{{ hits.length ? 'новые:' : 'ничего нового не нашлось' }}</span>
          <button v-for="(p, i) in hits.slice(0, 10)" :key="pairKey(p)" type="button"
                  class="ins-seg-add" :class="{ 'is-top': i === 0 }"
                  data-help="сегмент/добавить фразу" data-help-label="Добавить фразу"
                  @click="$emit('parts', parts.concat([{ word: p.word, phrase: p.phrase }]))">
            <i :style="{ background: dot(p) }"></i><b>{{ p.word }}</b> {{ p.phrase }}
          </button>
          <span v-if="hits.length > 10" class="tlabel">и ещё {{ hits.length - 10 }}</span>
        </span>
        <!-- Весь набранный состав, а не только совпавшее: поиск не должен прятать то,
             что уже накликано по шапкам. Совпавшие подсвечены — «она уже внутри». -->
        <span v-if="parts.length" class="ins-seg-sug ins-seg-had">
          <span class="tlabel">
            уже добавлены ({{ parts.length }}){{ mine.size ? ', совпало ' + mine.size : '' }}:
          </span>
          <span v-for="p in ordered" :key="pairKey(p)" class="ins-seg-chip"
                :class="{ 'is-hit': mine.has(pairKey(p)) }">
            <i :style="{ background: dot(p) }"></i><b>{{ p.word }}</b>
            <span class="ins-seg-ph">{{ p.phrase }}</span>
            <button type="button" class="ins-seg-x" data-help="сегмент/убрать фразу" data-help-label="Убрать фразу"
                    :aria-label="'убрать фразу ' + p.phrase + ' (' + p.word + ')'" @click="remove(p)">×</button>
          </span>
        </span>
      </template>
      <template v-else>
        <span v-if="!parts.length" class="tlabel">пусто: кликай по словам в шапке или ищи фразу</span>
        <span v-for="p in ordered" :key="pairKey(p)" class="ins-seg-chip">
          <i :style="{ background: dot(p) }"></i><b>{{ p.word }}</b>
          <span class="ins-seg-ph">{{ p.phrase }}</span>
          <button type="button" class="ins-seg-x" data-help="сегмент/убрать фразу" data-help-label="Убрать фразу"
                    :aria-label="'убрать фразу ' + p.phrase + ' (' + p.word + ')'" @click="remove(p)">×</button>
        </span>
      </template>
      <span v-if="parts.length" class="ins-seg-hit">{{ caught }}</span>
    </div>
  </div>
</template>

<script>
import { plural, segAutoName, segShortName, segMask } from '@/utils/segments';

const pairKey = p => p.word + '\u0000' + p.phrase;

export default {
  name: 'SegmentBar',
  props: {
    segments: { type: Array, required: true },       // К5[]
    editId: { type: String, default: null },         // id сегмента на правке или null — набираем новый
    parts: { type: Array, required: true },          // состав: сегмента на правке или черновика
    words: { type: Array, required: true },          // [{word, phrases}] в порядке матрицы
    countByWord: { type: Object, required: true },   // Object.create(null): слово → число фраз
    totalPhrases: { type: Number, required: true },
    nameMax: { type: Number, required: true },
  },
  emits: ['tab', 'new', 'rename', 'commit', 'done', 'parts'],
  data() {
    // draftName — имя нового столбца: переживает заход в правку вкладки и обратно.
    return { nameInput: '', draftName: '', find: '' };
  },
  computed: {
    editSeg() {
      return this.editId === null ? null : this.segments.find(s => s.id === this.editId) || null;
    },
    query() {
      return this.find.trim();
    },
    wordIndex() {
      const m = new Map();
      this.words.forEach((w, i) => m.set(w.word, i));
      return m;
    },
    everyPart() {
      const out = [];
      for (const w of this.words) for (const phrase of w.phrases) out.push({ word: w.word, phrase: phrase });
      return out;
    },
    full() {
      return this.parts.length > 0 && new Set(this.parts.map(pairKey)).size >= this.everyPart.length;
    },
    // Поиск предлагает только то, чего в составе ещё нет: искать уже добавленное бессмысленно.
    matched() {
      if (!this.query) return [];
      return this.everyPart.filter(p => segMask(this.query, p.word + ' ' + p.phrase));
    },
    hits() {
      const have = new Set(this.parts.map(pairKey));
      return this.matched.filter(p => !have.has(pairKey(p)));
    },
    mine() {
      const got = new Set(this.matched.map(pairKey));
      return new Set(this.parts.map(pairKey).filter(k => got.has(k)));
    },
    ordered() {
      const idx = w => (this.wordIndex.has(w) ? this.wordIndex.get(w) : Infinity);
      return this.parts.slice().sort((a, b) => idx(a.word) - idx(b.word) || this.kOf(a) - this.kOf(b));
    },
    fullName() {
      return this.parts.length ? segAutoName(this.parts, this.countByWord) : '';
    },
    shortName() {
      return this.parts.length ? segShortName(this.parts, this.nameMax, this.countByWord, this.totalPhrases) : '';
    },
    // Короткое имя идёт подсказкой, а не значением: записанное упиралось бы в предел,
    // и печатать своё стало бы нечем. Пустое поле = это имя.
    placeholder() {
      if (this.editId !== null) return this.shortName;
      return this.shortName || 'название столбца';
    },
    lenUsed() {
      return (this.nameInput || this.placeholder || '').length;
    },
    lenText() {
      // в правке счётчик всегда: у столбца, из которого убрали все фразы, имя всё равно есть
      return this.editId !== null || this.parts.length || this.nameInput ? this.lenUsed + '/' + this.nameMax : '';
    },
    lenTitle() {
      const limit = 'Предел ' + this.nameMax + ' знаков — по самому длинному слову в таблице.';
      const long = this.fullName.length > this.nameMax;
      if (this.editId !== null) {
        return limit + (long ? ' Полное имя состава «' + this.fullName + '» длиннее и в шапку не влезет.' : '');
      }
      return long
        ? 'Полное имя «' + this.fullName + '» длиннее ' + this.nameMax + ' знаков — столько занимает самое длинное '
          + 'слово в таблице, и шапка выше этого не растёт. В подсказке поля стоит короткое: '
          + 'оставишь поле пустым — столбец так и назовётся.'
        : limit;
    },
    caught() {
      const n = new Set(this.parts.map(p => p.phrase)).size;
      const w = new Set(this.parts.map(p => p.word)).size;
      return 'поймано: ' + n + ' ' + plural(n, ['фраза', 'фразы', 'фраз'])
        + ' в ' + w + ' ' + plural(w, ['слове', 'словах', 'словах']);
    },
  },
  watch: {
    // Сменился сегмент на правке — поле имени показывает его имя (своё, не авто).
    editId: {
      immediate: true,
      handler() {
        this.nameInput = this.editSeg ? (this.editSeg.auto ? '' : this.editSeg.name) : this.draftName;
        this.find = '';
      },
    },
  },
  methods: {
    pairKey: pairKey,
    autoName(parts) {
      return segAutoName(parts, this.countByWord);
    },
    kOf(p) {
      const i = this.wordIndex.get(p.word);
      return i === undefined ? 0 : Math.max(0, this.words[i].phrases.indexOf(p.phrase));
    },
    dot(p) {
      return 'var(--ph' + (this.kOf(p) % 6) + ')';
    },
    remove(p) {
      const k = pairKey(p);
      this.$emit('parts', this.parts.filter(q => pairKey(q) !== k));
    },
    onNew() {
      this.nameInput = '';
      this.draftName = '';
      this.find = '';
      this.$emit('new');
    },
    onName(event) {
      const v = event.target.value.slice(0, this.nameMax);
      this.nameInput = v;
      if (this.editId !== null) this.$emit('rename', v.trim());
      else this.draftName = v;
    },
    onNameBlur() {
      this.nameInput = this.nameInput.trim();
      if (this.editId === null) this.draftName = this.nameInput;
    },
    onNameEnter() {
      if (this.editId === null) this.commit();
    },
    commit() {
      if (!this.parts.length) return;
      this.$emit('commit', this.nameInput.trim());
      this.nameInput = '';
      this.draftName = '';
      this.find = '';
    },
    // Enter кладёт первую подсказку и очищает поле — готово к следующей.
    onFindEnter() {
      const h = this.hits[0];
      if (!h) return;
      this.find = '';
      this.$emit('parts', this.parts.concat([{ word: h.word, phrase: h.phrase }]));
    },
  },
};
</script>

<style>
/* Не scoped: классы с префиксом ins-, а bootstrap перекрывает короткие имена даже при scoped. */
.ins-segbar{display:flex; flex-direction:column; align-items:stretch; gap:6px; margin:2px 0 10px; font-size:12.5px;}
.ins-segrow{display:flex; flex-wrap:wrap; align-items:center; gap:6px;}
/* .ins-root впереди: иначе .ins-root .btn/.tgl из insights.css (та же специфичность, стоят позже)
   и .ins-root input.btn перебивают размеры, как было в r5-01. */
.ins-segbar .ins-seg-lab{min-width:112px;}
.ins-root .ins-segbar input.ins-seg-in{min-width:180px; padding:4px 8px; border-radius:7px; font-size:12.5px;
  cursor:text; text-align:left;}
.ins-root .ins-segbar input.ins-seg-find{min-width:230px;}
.ins-segbar .ins-seg-act{margin-inline-start:auto;}
/* line-height числом, не normal: с normal вкладка «окна» выходила 27 px, а «+ новый» 29
   (видимо, «+» из запасного шрифта), в П обе 29. 1.36 × 12.5 = 17 px строки → 29. */
.ins-root .ins-segbar .ins-seg-tab{max-width:150px; overflow:hidden; text-overflow:ellipsis; padding:5px 10px; font-size:12.5px;
  line-height:1.36;}
.ins-root .ins-segbar .ins-seg-new{border-style:dashed;}
.ins-seg-len{min-width:34px; color:var(--text-muted); font-size:12px;}
.ins-seg-len.is-warn{color:#d4483b; font-weight:700;}
.ins-seg-hit{color:var(--text-muted); font-size:12px;}
.ins-seg-chip{display:inline-flex; align-items:center; gap:4px; padding:2px 7px; border-radius:999px;
  border:1px solid var(--border); background:var(--surface-1); font-size:12px;}
.ins-seg-chip i, .ins-seg-add i{width:8px; height:8px; border-radius:50%; display:inline-block;}
.ins-seg-chip b, .ins-seg-add b{font-weight:600;}
.ins-seg-chip .ins-seg-ph{color:var(--text-muted);}
.ins-seg-chip .ins-seg-x{border:0; background:none; padding:0 2px; font:inherit; line-height:1;
  color:var(--text-muted); cursor:pointer; font-size:13px; border-radius:4px;}
.ins-seg-chip .ins-seg-x:hover{color:var(--text-primary);}
.ins-seg-sug{display:flex; flex-wrap:wrap; gap:4px; align-items:center; padding:3px 5px; border-radius:8px;
  background:var(--cell-0);}
.ins-seg-add{display:inline-flex; align-items:center; gap:4px; font:inherit; font-size:12px;
  padding:2px 7px; border-radius:999px; cursor:pointer;
  border:1px dashed var(--border); background:var(--surface-1); color:var(--text-primary);}
.ins-seg-add:hover{border-style:solid; border-color:var(--accent);}
.ins-seg-add.is-top{border-color:var(--accent);}
/* уже добавленные при поиске — отдельной полосой ниже, приглушённо и с крестиками */
.ins-seg-sug.ins-seg-had{background:none; border:1px dashed var(--border); margin-top:4px;}
.ins-seg-sug.ins-seg-had .ins-seg-chip{background:none;}
.ins-seg-sug.ins-seg-had .ins-seg-chip.is-hit{border-color:var(--accent); background:var(--surface-1);}
.ins-seg-sug.ins-seg-had .ins-seg-chip.is-hit b{color:var(--accent);}
</style>
